import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Chip,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import type { User as UserType } from '@/types';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

interface SubtaskFormData {
  title: string;
  estimatedTime: number; // minutes
  dueDate: Dayjs | null;
  responsible: string[];
}

interface SubtaskFormProps {
  index: number;
  data: SubtaskFormData;
  onChange: (index: number, data: SubtaskFormData) => void;
  onRemove: (index: number) => void;
  availableUsers: UserType[]; // RACI pool from parent
  parentDueDate: Dayjs;
  parentStartDate: Dayjs | null;
  remainingTime: number; // minutes
  errors?: {
    title?: string;
    estimatedTime?: string;
    dueDate?: string;
    responsible?: string;
  };
}

function userDisplayName(u: UserType): string {
  return u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
}

/**
 * SubtaskForm
 * Single subtask entry form with validation
 * Enforces time budget and RACI inheritance
 */
const SubtaskForm: React.FC<SubtaskFormProps> = ({
  index,
  data,
  onChange,
  onRemove,
  availableUsers,
  parentDueDate,
  parentStartDate,
  remainingTime,
  errors,
}) => {
  const handleChange = (field: keyof SubtaskFormData, value: any) => {
    onChange(index, { ...data, [field]: value });
  };

  const exceedsBudget = remainingTime < 0;
  const exceedBy = exceedsBudget ? Math.abs(remainingTime) : 0;
  const formatHm = (minutes: number) => {
    const abs = Math.abs(Math.round(minutes));
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: exceedsBudget ? 'warning.light' : 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Subtask {index + 1}
        </Typography>
        <IconButton size="small" onClick={() => onRemove(index)} color="error">
          <X size={16} />
        </IconButton>
      </Box>

      {/* Title */}
      <TextField
        fullWidth
        size="small"
        label="Subtask Title"
        value={data.title}
        onChange={(e) => handleChange('title', e.target.value)}
        error={Boolean(errors?.title)}
        helperText={errors?.title}
        sx={{ mb: 2 }}
        placeholder="e.g., Setup database schema"
      />

      {/* Bottom Row: Time, Due Date, Assignee */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {/* Estimated Time: Hours + Minutes (stored as minutes) */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <TextField
              size="small"
              label="Hours"
              type="number"
              value={Math.floor((data.estimatedTime || 0) / 60) || ''}
              onChange={(e) => {
                const raw = e.target.value;
                const mins = (data.estimatedTime || 0) % 60;
                if (raw === '') {
                  handleChange('estimatedTime', mins);
                  return;
                }
                const h = parseInt(raw, 10);
                if (!Number.isNaN(h) && h >= 0) {
                  handleChange('estimatedTime', h * 60 + mins);
                }
              }}
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              size="small"
              label="Minutes"
              type="number"
              value={(data.estimatedTime || 0) % 60 || ''}
              onChange={(e) => {
                const raw = e.target.value;
                const hours = Math.floor((data.estimatedTime || 0) / 60);
                if (raw === '') {
                  handleChange('estimatedTime', hours * 60);
                  return;
                }
                const m = parseInt(raw, 10);
                if (!Number.isNaN(m) && m >= 0) {
                  handleChange('estimatedTime', hours * 60 + Math.min(59, m));
                }
              }}
              error={Boolean(errors?.estimatedTime)}
              helperText={
                errors?.estimatedTime ||
                (exceedsBudget
                  ? `Over parent by ${formatHm(exceedBy)}`
                  : `${formatHm(Math.max(0, remainingTime))} left`)
              }
              inputProps={{ min: 0, max: 59, step: 1 }}
            />
          </Box>
          {exceedsBudget && (
            <Chip
              size="small"
              color="warning"
              label={`Exceeded +${formatHm(exceedBy)}`}
              sx={{ mt: 0.75 }}
            />
          )}
        </Box>

        {/* Due Date */}
        <Box sx={{ flex: '1 1 180px', minWidth: '180px' }}>
          <DateTimePicker
            label="Due Date"
            value={data.dueDate}
            onChange={(newValue) => handleChange('dueDate', newValue)}
            maxDate={parentDueDate}
            minDate={parentStartDate || undefined}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: Boolean(errors?.dueDate),
                helperText: errors?.dueDate,
              },
            }}
          />
        </Box>

        {/* Responsible (from parent RACI pool) */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <AppSelect
            label="Responsible"
            size="small"
            error={Boolean(errors?.responsible)}
            formControlProps={{ sx: { width: '100%' } }}
            multiple
            value={data.responsible}
            onChange={(e) => handleChange('responsible', e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((userId) => {
                  const user = availableUsers.find((u) => u.id === userId);
                  return (
                    <Chip
                      key={userId}
                      label={user ? userDisplayName(user) : userId}
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
          >
            {availableUsers.map((user) => (
              <AppMenuItem key={user.id} value={user.id}>
                {userDisplayName(user)}
              </AppMenuItem>
            ))}
          </AppSelect>
          {errors?.responsible && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {errors.responsible}
            </Typography>
          )}
          {availableUsers.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
              Set parent task RACI first
            </Typography>
          )}
        </Box>
      </Box>

      {/* Exceed tag already shown under time field */}
    </Box>
  );
};

export default SubtaskForm;
